<template>
  <v-main>
    <v-container fluid>
      <!-- Page Header + Filters -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="pb-2 pt-3 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <h2 class="text-h6 font-weight-bold primary--text mb-0">
                    Transaction Audit Report
                  </h2>
                  <p class="text-caption grey--text ma-0 mt-1">
                    Reconcile appointment, consultation &amp; order transactions with doctor/seller payouts
                  </p>
                </v-col>
                <v-col cols="12" md="4" class="pa-0 d-flex justify-end">
                  <v-btn
                    x-small
                    color="success"
                    outlined
                    @click="exportToExcel"
                    :loading="isExporting"
                    :disabled="ledger.length === 0"
                  >
                    <v-icon left x-small>mdi-microsoft-excel</v-icon>
                    Export Audit
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider class="mx-4"></v-divider>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-filter-variant</v-icon>
                    <span class="text-body-2 font-weight-medium">Filter Report</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip v-if="hasActiveFilters" color="info" x-small class="mr-2">
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- From Date -->
                <v-col cols="12" md="2">
                  <v-menu
                    v-model="fromMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="filter.from_date"
                        label="From Date"
                        prepend-inner-icon="mdi-calendar-start"
                        readonly
                        outlined
                        dense
                        clearable
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="(filter.from_date = ''), (page = 1), getData()"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.from_date"
                      @input="(fromMenu = false), (page = 1), getData()"
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <!-- To Date -->
                <v-col cols="12" md="2">
                  <v-menu
                    v-model="toMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="filter.to_date"
                        label="To Date"
                        prepend-inner-icon="mdi-calendar-end"
                        readonly
                        outlined
                        dense
                        clearable
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="(filter.to_date = ''), (page = 1), getData()"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.to_date"
                      @input="(toMenu = false), (page = 1), getData()"
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Source -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.source"
                    :items="sourceOptions"
                    label="Source"
                    prepend-inner-icon="mdi-shape-outline"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Status -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.status"
                    :items="statusOptions"
                    label="Status"
                    prepend-inner-icon="mdi-check-circle"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- User -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.user_id"
                    :items="userFilterItems"
                    :item-text="getUserDisplayText"
                    item-value="id"
                    :filter="filterUserByPhone"
                    :loading="mastersLoading"
                    label="User"
                    prepend-inner-icon="mdi-account"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  >
                    <template #item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ item.user_name }}
                          <span v-if="item.position" class="grey--text text--darken-1 text-caption ml-1">
                            ({{ item.position.name || item.position }})
                          </span>
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="item.phone" class="text-caption">
                          {{ item.phone }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Summary Stat Cards -->
      <v-row dense class="mb-1">
        <v-col cols="6" sm="6" md="3" v-for="(stat, i) in summaryStats" :key="'stat-' + i">
          <v-card class="stat-card" :class="'stat-' + stat.color" elevation="1">
            <v-card-text class="pa-3">
              <div class="d-flex align-center">
                <div class="stat-icon-wrap mr-3" :class="'bg-' + stat.color">
                  <v-icon color="white">{{ stat.icon }}</v-icon>
                </div>
                <div class="flex-grow-1" style="min-width: 0">
                  <div class="stat-value">{{ stat.value }}</div>
                  <div class="stat-label">{{ stat.label }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- By-Source Breakdown -->
      <v-row dense class="mb-2">
        <v-col cols="12" md="8">
          <v-card elevation="2" class="h-100">
            <v-card-title class="py-2 px-4 text-body-1 font-weight-medium">
              <v-icon class="mr-2" color="primary" size="20">mdi-chart-box-outline</v-icon>
              Breakdown by Source
            </v-card-title>
            <v-divider></v-divider>
            <v-simple-table dense class="breakdown-table">
              <thead>
                <tr>
                  <th class="text-left">Source</th>
                  <th class="text-center">Txns</th>
                  <th class="text-right">Money In</th>
                  <th class="text-right">Money Out</th>
                  <th class="text-right">Commission</th>
                  <th class="text-right">Refunds</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(src, i) in bySource" :key="'src-' + i">
                  <td>
                    <v-chip x-small outlined :color="sourceColor(src.label)">{{ src.label }}</v-chip>
                  </td>
                  <td class="text-center">{{ src.count }}</td>
                  <td class="text-right">{{ formatAmount(src.money_in) }}</td>
                  <td class="text-right">{{ formatAmount(src.money_out) }}</td>
                  <td class="text-right success--text">{{ formatAmount(src.commission) }}</td>
                  <td class="text-right error--text">{{ formatAmount(src.refunds) }}</td>
                </tr>
                <tr class="font-weight-bold total-row">
                  <td>TOTAL</td>
                  <td class="text-center">{{ totals.transaction_count }}</td>
                  <td class="text-right">{{ formatAmount(totals.money_in) }}</td>
                  <td class="text-right">{{ formatAmount(totals.money_out) }}</td>
                  <td class="text-right success--text">{{ formatAmount(totals.platform_commission) }}</td>
                  <td class="text-right error--text">{{ formatAmount(totals.refunds) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>

        <!-- Payout reconciliation -->
        <v-col cols="12" md="4">
          <v-card elevation="2" class="h-100">
            <v-card-title class="py-2 px-4 text-body-1 font-weight-medium">
              <v-icon class="mr-2" color="primary" size="20">mdi-bank-transfer-out</v-icon>
              Payouts
            </v-card-title>
            <v-divider></v-divider>
            <v-list dense class="py-0">
              <v-list-item>
                <v-list-item-content>Total ({{ payouts.total_count }})</v-list-item-content>
                <v-list-item-action class="font-weight-bold">{{ formatAmount(payouts.total_amount) }}</v-list-item-action>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item>
                <v-list-item-content class="success--text">Completed ({{ payouts.completed_count }})</v-list-item-content>
                <v-list-item-action class="success--text">{{ formatAmount(payouts.completed_amount) }}</v-list-item-action>
              </v-list-item>
              <v-list-item>
                <v-list-item-content class="warning--text">Pending ({{ payouts.pending_count }})</v-list-item-content>
                <v-list-item-action class="warning--text">{{ formatAmount(payouts.pending_amount) }}</v-list-item-action>
              </v-list-item>
              <v-list-item>
                <v-list-item-content class="error--text">Failed ({{ payouts.failed_count }})</v-list-item-content>
                <v-list-item-action class="error--text">{{ formatAmount(payouts.failed_amount) }}</v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>

      <!-- Ledger Table -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-format-list-bulleted</v-icon>
                    <span class="text-body-1 font-weight-medium">Audit Ledger</span>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-receipt-text</v-icon>
                      {{ count }} {{ count === 1 ? "Entry" : "Entries" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{ Math.min(page * itemsPerPage, count) }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-1">Show:</span>
                    <v-btn-toggle v-model="selectedItemsPerPage" dense @change="changeItemsPerPageToggle">
                      <v-btn x-small outlined :value="10">10</v-btn>
                      <v-btn x-small outlined :value="25">25</v-btn>
                      <v-btn x-small outlined :value="50">50</v-btn>
                    </v-btn-toggle>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-card-text class="pa-0">
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader />
                  <p class="mt-3 grey--text">Generating audit report...</p>
                </div>
              </section>

              <v-data-table
                v-else
                :headers="headers"
                :items="ledger"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <template #[`item.date`]="{ item }">
                  <span class="text-caption">{{ item.date }}</span>
                </template>

                <template #[`item.source`]="{ item }">
                  <status-chip x-small :color="sourceColor(item.source)" :label="String(item.source)" />
                </template>

                <template #[`item.user_name`]="{ item }">
                  <div>
                    <div class="text-body-2 font-weight-medium">{{ item.user_name }}</div>
                    <div class="text-caption grey--text" v-if="item.user_position">{{ item.user_position }}</div>
                  </div>
                </template>

                <template #[`item.direction`]="{ item }">
                  <status-chip x-small :color="directionColor(item)" :icon="directionIcon(item.direction)" :label="String(item.is_refund ? 'Refund' : item.direction)" />
                </template>

                <template #[`item.net_amount`]="{ item }">
                  <div class="text-body-2 font-weight-bold">{{ formatAmount(item.net_amount) }}</div>
                  <div class="text-caption grey--text" v-if="item.commission_amount">
                    comm {{ formatAmount(item.commission_amount) }}
                  </div>
                </template>

                <template #[`item.status_label`]="{ item }">
                  <status-chip x-small :color="statusColor(item.status)" :label="String(item.status_label)" />
                </template>

                <template #[`item.remarks`]="{ item }">
                  <div
                    class="text-caption grey--text text--darken-1"
                    style="max-width: 240px; white-space: normal; line-height: 1.4"
                  >
                    {{ item.remarks || "—" }}
                  </div>
                </template>

                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3">mdi-file-search-outline</v-icon>
                    <h3 class="text-h6 grey--text mt-4 mb-2">No Transactions Found</h3>
                    <p class="grey--text mb-4">
                      No financial activity matches the selected filters for this period.
                    </p>
                    <v-btn v-if="hasActiveFilters" color="primary" outlined @click="clearAllFilters">
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <v-col cols="12" md="3" class="pa-0">
                  <span class="text-caption grey--text">
                    {{ (page - 1) * itemsPerPage + 1 }}-{{ Math.min(page * itemsPerPage, count) }}
                    of {{ count }}
                  </span>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-btn x-small outlined :disabled="page <= 1" @click="page--" class="mr-2">
                      <v-icon x-small left>mdi-chevron-left</v-icon>Prev
                    </v-btn>
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>
                    <v-btn x-small outlined :disabled="page >= paginationLength" @click="page++" class="ml-2">
                      Next<v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "TransactionAuditReport",
  data() {
    return {
      filter: {
        from_date: "",
        to_date: "",
        source: "",
        status: "",
        user_id: "",
      },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      paginationLength: 0,
      count: 0,
      jumpToPage: 1,
      isLoading: false,
      isExporting: false,
      mastersLoading: false,
      fromMenu: false,
      toMenu: false,
      ledger: [],
      userFilterItems: [],
      summary: {
        by_source: [],
        totals: {
          transaction_count: 0,
          money_in: 0,
          money_out: 0,
          refunds: 0,
          platform_commission: 0,
        },
        payouts: {
          total_count: 0, total_amount: 0,
          completed_count: 0, completed_amount: 0,
          pending_count: 0, pending_amount: 0,
          failed_count: 0, failed_amount: 0,
        },
      },
      sourceOptions: [
        { text: "All Sources", value: "all" },
        { text: "Appointments", value: "appointment" },
        { text: "Consultations", value: "consultation" },
        { text: "Orders", value: "order" },
        { text: "Payouts", value: "payout" },
      ],
      statusOptions: [
        { text: "Pending", value: "0" },
        { text: "Completed", value: "1" },
        { text: "Failed", value: "2" },
      ],
      headers: [
        { text: "#", value: "sr_no", sortable: false, width: "50px", align: "center" },
        { text: "Date", value: "date", sortable: false, width: "140px" },
        { text: "Source", value: "source", sortable: false, width: "110px" },
        { text: "Reference", value: "reference", sortable: false, width: "120px" },
        { text: "User", value: "user_name", sortable: false, width: "160px" },
        { text: "Direction", value: "direction", sortable: false, width: "100px" },
        { text: "Amount", value: "net_amount", sortable: false, width: "120px" },
        { text: "Method", value: "method", sortable: false, width: "110px" },
        { text: "Status", value: "status_label", sortable: false, width: "100px" },
        { text: "Remarks", value: "remarks", sortable: false, width: "240px" },
      ],
    };
  },

  computed: {
    bySource() {
      return this.summary.by_source || [];
    },
    totals() {
      return this.summary.totals || {};
    },
    payouts() {
      return this.summary.payouts || {};
    },
    summaryStats() {
      const t = this.totals;
      const p = this.payouts;
      return [
        { icon: "mdi-cash-plus", value: this.formatAmount(t.money_in), label: "Money Collected", color: "blue" },
        { icon: "mdi-cash-minus", value: this.formatAmount(t.money_out), label: "Provider Earnings", color: "purple" },
        { icon: "mdi-percent-outline", value: this.formatAmount(t.platform_commission), label: "Platform Commission", color: "green" },
        { icon: "mdi-bank-transfer-out", value: this.formatAmount(p.completed_amount), label: "Payouts Disbursed", color: "orange" },
      ];
    },
    hasActiveFilters() {
      return (
        this.filter.from_date ||
        this.filter.to_date ||
        this.filter.source ||
        this.filter.status ||
        this.filter.user_id
      );
    },
  },

  watch: {
    page: {
      handler(newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
  },

  mounted() {
    this.jumpToPage = this.page;
    this.getData();
    this.getMasters();
  },

  methods: {
    async getData() {
      this.isLoading = true;
      const params = {
        page: this.page,
        rowsPerPage: this.itemsPerPage,
        from_date: this.filter.from_date || "",
        to_date: this.filter.to_date || "",
        source: this.filter.source || "",
        status: this.filter.status || "",
        user_id: this.filter.user_id || "",
      };
      try {
        const response = await axios.get("reports/transaction-audit", { params });
        this.ledger = response.data.data;
        this.count = response.data.count;
        this.summary = response.data.summary;
        this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      } finally {
        this.isLoading = false;
      }
    },

    async getMasters() {
      this.mastersLoading = true;
      try {
        const response = await axios.get("masters?context=transactions");
        if (response.data?.users) {
          this.userFilterItems = response.data.users;
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      } finally {
        this.mastersLoading = false;
      }
    },

    async exportToExcel() {
      this.isExporting = true;
      const params = {
        from_date: this.filter.from_date || "",
        to_date: this.filter.to_date || "",
        source: this.filter.source || "",
        status: this.filter.status || "",
        user_id: this.filter.user_id || "",
      };
      try {
        const response = await axios.get("reports/transaction-audit/excel", {
          params,
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `transaction_audit_${new Date().toISOString().split("T")[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      } finally {
        this.isExporting = false;
      }
    },

    getUserDisplayText(user) {
      const pos = user.position?.name || (typeof user.position === "string" ? user.position : "");
      return pos ? `${user.user_name} (${pos})` : user.user_name;
    },

    filterUserByPhone(item, queryText) {
      const q = queryText.toLowerCase();
      const name = String(item.user_name || "").toLowerCase();
      const phone = String(item.phone || "").toLowerCase();
      const pos = String(item.position?.name || item.position || "").toLowerCase();
      return name.includes(q) || phone.includes(q) || pos.includes(q);
    },

    formatAmount(amount) {
      const n = Number(amount) || 0;
      return "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n);
    },

    sourceColor(label) {
      const map = {
        Appointment: "indigo",
        Consultation: "purple",
        Order: "teal",
        Payout: "orange",
      };
      return map[label] || "grey";
    },

    directionColor(item) {
      if (item.is_refund) return "error";
      if (item.direction === "Payout") return "orange";
      return item.direction === "Credit" ? "success" : "blue-grey";
    },

    directionIcon(direction) {
      if (direction === "Credit") return "mdi-plus";
      if (direction === "Payout") return "mdi-bank-transfer-out";
      return "mdi-minus";
    },

    statusColor(status) {
      return { 0: "warning", 1: "success", 2: "error" }[status] || "grey";
    },

    clearAllFilters() {
      this.filter.from_date = "";
      this.filter.to_date = "";
      this.filter.source = "";
      this.filter.status = "";
      this.filter.user_id = "";
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.from_date) count++;
      if (this.filter.to_date) count++;
      if (this.filter.source) count++;
      if (this.filter.status) count++;
      if (this.filter.user_id) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (targetPage && targetPage >= 1 && targetPage <= this.paginationLength) {
        this.page = targetPage;
      } else {
        this.jumpToPage = this.page;
      }
    },
  },
};
</script>

<style scoped>
.v-container {
  max-width: 100% !important;
}

.h-100 {
  height: 100%;
}

/* Stat cards */
.stat-card {
  border-radius: 10px;
  transition: all 0.2s ease;
}
.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
}
.stat-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-value {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stat-label {
  font-size: 0.72rem;
  color: #78909c;
  margin-top: 2px;
}
.bg-blue { background: #1976d2; }
.bg-purple { background: #8e24aa; }
.bg-green { background: #43a047; }
.bg-orange { background: #fb8c00; }

/* Breakdown table */
.breakdown-table >>> th {
  font-size: 0.72rem !important;
  height: 34px !important;
}
.breakdown-table >>> td {
  font-size: 0.78rem !important;
  height: 38px !important;
}
.total-row td {
  border-top: 2px solid #e0e0e0 !important;
  background: #fafafa;
}

/* Enhanced Table Styling */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}
.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}
.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}
.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
}
.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
}

.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
}
.v-btn--x-small {
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}
.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}
.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}
.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

.v-chip {
  font-weight: 500;
  font-size: 0.7rem !important;
}

.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}

@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }
}
</style>
